import intl from "react-intl-universal";
import { TbEye, TbKey, TbPlus, TbTrash } from "react-icons/tb";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  revoked: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
};

const getLocale = () => intl.getInitOptions?.()?.currentLocale || "pt-BR";

const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (date) =>
  new Intl.DateTimeFormat(getLocale(), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

const formatRelativeTime = (date) => {
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSeconds);
  const rtf = new Intl.RelativeTimeFormat(getLocale(), { numeric: "auto" });
  if (abs < 60) return rtf.format(diffSeconds, "second");
  if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSeconds / 3600), "hour");
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSeconds / 86400), "day");
  if (abs < 86400 * 365) return rtf.format(Math.round(diffSeconds / (86400 * 30)), "month");
  return rtf.format(Math.round(diffSeconds / (86400 * 365)), "year");
};

const formatDate = (value) => {
  const date = toValidDate(value);
  if (!date) return intl.get("baas.api.keys.neverUsed");
  return formatDateTime(date);
};

const formatLastUsed = (value) => {
  const date = toValidDate(value);
  if (!date) {
    return { label: intl.get("baas.api.keys.neverUsed"), title: undefined };
  }
  const datetime = formatDateTime(date);
  const relative = formatRelativeTime(date);
  const label =
    intl.get("baas.api.keys.lastUsedDisplay", { relative, datetime }) ||
    `${relative} · ${datetime}`;
  return { label, title: datetime };
};

const ApiKeyRow = ({
  item,
  onRevoke,
  onReveal,
  manageDisabled = false,
  revealEnabled = false,
}) => {
  const lastUsed = formatLastUsed(item.lastUsedAt);
  const showReveal = item.status === "active" && revealEnabled && typeof onReveal === "function";
  const showRevoke = item.status === "active" && !manageDisabled;

  return (
    <div className="grid grid-cols-1 gap-3 border-b border-gray-100 px-4 py-4 last:border-b-0 dark:border-brand-dark-border lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.6fr)_auto] lg:items-center">
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60 dark:border-brand-dark-border dark:bg-brand-dark-elevated">
        <TbKey className="h-4 w-4 text-brand-dark dark:text-gray-100" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">{item.name}</p>
        <p className="text-[11px] text-gray-400">
          {item.environment === "sandbox" || item.environment === "dev"
            ? `${intl.get("baas.api.environment.sandbox")} (HML)`
            : intl.get(`baas.api.environment.${item.environment}`)}
        </p>
      </div>
    </div>

    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
        {intl.get("baas.api.keys.prefix")}
      </p>
      <code className="text-sm text-gray-600 dark:text-gray-300">{item.prefix}••••••••</code>
    </div>

    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
        {intl.get("baas.api.keys.created")}
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-300">{formatDate(item.createdAt)}</p>
    </div>

    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
        {intl.get("baas.api.keys.lastUsed")}
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-300" title={lastUsed.title}>
        {lastUsed.label}
      </p>
    </div>

    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
        {intl.get("baas.api.keys.status")}
      </p>
      <span
        className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
          STATUS_STYLES[item.status] ?? STATUS_STYLES.revoked
        }`}
      >
        {intl.get(`baas.api.keys.statusValue.${item.status}`)}
      </span>
    </div>

    <div className="flex flex-wrap justify-start gap-2 lg:justify-end">
      {showReveal ? (
        <button
          type="button"
          className="btn-outline shrink-0"
          onClick={() => onReveal(item)}
          title={intl.get("baas.api.keys.reveal")}
        >
          <TbEye className="h-4 w-4" />
          {intl.get("baas.api.keys.reveal")}
        </button>
      ) : null}
      {showRevoke ? (
        <button
          type="button"
          className="btn-outline shrink-0 !text-red-600"
          onClick={() => onRevoke(item.id)}
        >
          <TbTrash className="h-4 w-4" />
          {intl.get("baas.api.keys.revoke")}
        </button>
      ) : null}
      {!showReveal && !showRevoke ? (
        item.status === "active" ? (
          <span className="text-xs text-gray-400">—</span>
        ) : (
          <span className="text-xs text-gray-400">{intl.get("baas.api.keys.revoked")}</span>
        )
      ) : null}
    </div>
  </div>
  );
};

export const BaasApiKeysPanel = ({
  apiKeys,
  onGenerate,
  onRevoke,
  onReveal,
  manageDisabled = false,
  revealEnabled = false,
  emptyMessage,
  sectionSubtitle,
}) => (
  <div className="min-w-0">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.api.keys.sectionTitle")}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {sectionSubtitle || intl.get("baas.api.keys.sectionSubtitle")}
        </p>
      </div>
      {!manageDisabled ? (
        <button
          type="button"
          className="btn-primary shrink-0"
          onClick={onGenerate}
        >
          <TbPlus className="h-4 w-4" />
          {intl.get("baas.api.keys.generate")}
        </button>
      ) : null}
    </div>

    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="hidden border-b border-gray-100 bg-gray-50/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 lg:grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.6fr)_auto]">
        <span>{intl.get("baas.api.keys.nameLabel")}</span>
        <span>{intl.get("baas.api.keys.prefix")}</span>
        <span>{intl.get("baas.api.keys.created")}</span>
        <span>{intl.get("baas.api.keys.lastUsed")}</span>
        <span>{intl.get("baas.api.keys.status")}</span>
        <span className="text-right">{intl.get("baas.api.keys.actions")}</span>
      </div>

      {apiKeys.length ? (
        apiKeys.map((item) => (
          <ApiKeyRow
            key={item.id}
            item={item}
            onRevoke={onRevoke}
            onReveal={onReveal}
            manageDisabled={manageDisabled}
            revealEnabled={revealEnabled}
          />
        ))
      ) : (
        <div className="app-empty-state p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          {emptyMessage || intl.get("baas.api.keys.empty")}
        </div>
      )}
    </div>
  </div>
);
